import { cn } from "./classNames-DcGnTh2N.js";
import { Tooltip, copyToClipboard } from "./utils-DwC9u0tM.js";
import { Check, Copy, Eye, EyeOff } from "./icons-ZIeZXXS7.js";
import { Label } from "./internal/components/Label.js";
import { createContext, use, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/blocks/CopyField/CopyContext.tsx
const CopyContext = createContext(null);
const useCopyContext = () => {
	const ctx = use(CopyContext);
	if (!ctx) throw new Error("CopyField.* components must be used within a CopyField.Root");
	return ctx;
};
//#endregion
//#region src/blocks/CopyField/CopyBox.tsx
const LINE_HEIGHT_REM = 1.25;
const VERTICAL_PADDING_REM = 1.25;
const COPY_HINT_DELAY_MS = 2e3;
/**
* The clickable copy target. Click anywhere to copy; text stays drag-selectable.
*
* Rendered as a `role="button"` div (not a native `<button>`) so it can host
* genuinely interactive children like `CopyField.Reveal` — nesting a `<button>`
* inside a `<button>` is invalid HTML.
*/
const CopyBox = ({ className, children }) => {
	const { copied, handleCopy, mono, minLines, maxLines, boxId, labelId, hasLabel, hoverHint } = useCopyContext();
	const multiline = maxLines > 1;
	const multilineStyle = multiline ? {
		minHeight: `${minLines * LINE_HEIGHT_REM + VERTICAL_PADDING_REM}rem`,
		maxHeight: `${maxLines * LINE_HEIGHT_REM + VERTICAL_PADDING_REM}rem`
	} : void 0;
	const handleKeyDown = (event) => {
		if (event.target !== event.currentTarget) return;
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		handleCopy();
	};
	return /* @__PURE__ */ jsx(Tooltip, {
		triggerAsChild: true,
		placement: "top",
		tooltip: copied ? "Copied" : hoverHint ? "Copy" : null,
		forceShow: copied,
		delayDuration: COPY_HINT_DELAY_MS,
		children: /* @__PURE__ */ jsx("div", {
			role: "button",
			tabIndex: 0,
			id: boxId,
			"aria-labelledby": hasLabel ? labelId : void 0,
			onClick: handleCopy,
			onKeyDown: handleKeyDown,
			style: multilineStyle,
			className: cn("tw:group tw:relative tw:flex tw:w-fit tw:max-w-full tw:cursor-pointer tw:gap-2 tw:rounded-2xl tw:[corner-shape:superellipse(2)] tw:border tw:border-border tw:bg-card tw:px-3 tw:py-2.5 tw:text-left tw:shadow-sm tw:transition-colors tw:hover:bg-muted", multiline ? "tw:items-start tw:overflow-auto" : "tw:items-center tw:overflow-hidden", mono && "tw:font-mono tw:text-[13px]", className),
			children
		})
	});
};
//#endregion
//#region src/blocks/CopyField/CopyButton.tsx
/** Copy affordance icon — shows Copy/Check with feedback. */
const CopyButton = ({ className }) => {
	const { copied, maxLines } = useCopyContext();
	return /* @__PURE__ */ jsx("span", {
		"aria-hidden": true,
		className: cn("tw:pointer-events-none tw:flex tw:shrink-0 tw:items-center tw:text-muted-foreground tw:group-hover:text-foreground", maxLines > 1 && "tw:self-start tw:pt-0.5", className),
		children: copied ? /* @__PURE__ */ jsx(Check, { className: "tw:size-4" }) : /* @__PURE__ */ jsx(Copy, { className: "tw:size-4" })
	});
};
//#endregion
//#region src/blocks/CopyField/CopyChildren.tsx
/** The displayed value. Truncates on a single line, wraps when multi-line. */
const CopyChildren = ({ children, className }) => {
	const { maxLines } = useCopyContext();
	return /* @__PURE__ */ jsx("span", {
		className: cn("tw:min-w-0 tw:flex-1 tw:select-text tw:text-card-foreground", maxLines > 1 ? "tw:whitespace-pre-wrap tw:break-words" : "tw:truncate", className),
		children
	});
};
//#endregion
//#region src/blocks/CopyField/CopyFooter.tsx
/** Small helper text below the box (mirrors `FormGroup.Help` / `HelpBlock` styling). */
const CopyFooter = ({ children, className }) => {
	if (!children) return null;
	return /* @__PURE__ */ jsx("p", {
		className: cn("tw:m-0 tw:text-xs tw:text-muted-foreground", className),
		children
	});
};
//#endregion
//#region src/blocks/CopyField/CopyHidden.tsx
const MASK = "*".repeat(12);
/**
* Masks its children with asterisks. The real value still lives on `CopyField.Root`,
* so copy always copies the real value regardless of mask state.
*/
const CopyHidden = ({ children, revealable = true, className }) => {
	const { hidden, registerRevealable } = useCopyContext();
	useEffect(() => {
		if (!revealable) return void 0;
		return registerRevealable();
	}, [revealable, registerRevealable]);
	const masked = revealable ? hidden : true;
	return /* @__PURE__ */ jsx("span", {
		className: cn("tw:select-none", className),
		children: masked ? MASK : children
	});
};
//#endregion
//#region src/blocks/CopyField/CopyLabel.tsx
/**
* Visible label above the box. The label carries an id and the box is wired via
* `aria-labelledby` (not `htmlFor`, which would turn label clicks into a copy).
*
* Row layout: label text, then `leading` (gap), then `trailing` pushed to the far
* edge with the space between them growing.
*/
const CopyLabel = ({ children, leading, trailing, className }) => {
	const { labelId, registerLabel } = useCopyContext();
	useEffect(() => registerLabel(), [registerLabel]);
	return /* @__PURE__ */ jsxs("div", {
		className: cn("tw:flex tw:items-center tw:gap-1", className),
		children: [
			/* @__PURE__ */ jsx(Label, {
				id: labelId,
				children
			}),
			leading,
			trailing != null && /* @__PURE__ */ jsx("span", {
				className: "tw:ml-auto tw:flex tw:items-center",
				children: trailing
			})
		]
	});
};
//#endregion
//#region src/blocks/CopyField/CopyPrefix.tsx
/** Muted, non-selectable prefix addon with a trailing divider (not part of the copied value). */
const CopyPrefix = ({ children, className }) => /* @__PURE__ */ jsx("span", {
	"aria-hidden": true,
	className: cn("tw:shrink-0 tw:select-none tw:self-stretch tw:border-r tw:border-border tw:pr-2 tw:text-muted-foreground", className),
	children
});
//#endregion
//#region src/blocks/CopyField/CopyReveal.tsx
/** Eye toggle that reveals/masks `CopyField.Hidden` content. Renders nothing without revealable content. */
const CopyReveal = ({ className, subject = "value" }) => {
	const { hidden, toggleHidden, hasRevealable, maxLines } = useCopyContext();
	if (!hasRevealable) return null;
	const handleClick = (event) => {
		event.stopPropagation();
		toggleHidden();
	};
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		onClick: handleClick,
		"aria-label": `${hidden ? "Show" : "Hide"} ${subject}`,
		className: cn("tw:flex tw:shrink-0 tw:items-center tw:text-muted-foreground tw:hover:text-foreground", maxLines > 1 && "tw:self-start tw:pt-0.5", className),
		children: hidden ? /* @__PURE__ */ jsx(Eye, { className: "tw:size-4" }) : /* @__PURE__ */ jsx(EyeOff, { className: "tw:size-4" })
	});
};
//#endregion
//#region src/blocks/CopyField/CopyRoot.tsx
/**
* Container for the Copy block. Owns the copied `value`, copy/mask state, and the
* shared context consumed by `CopyField.Box`, `CopyField.Reveal`, `CopyField.Button`, etc.
*/
const CopyRoot = ({ value, mono = false, onCopy, hiddenByDefault = false, minLines = 1, maxLines, hoverHint = true, className, children }) => {
	const [copied, setCopied] = useState(false);
	const [hidden, setHidden] = useState(hiddenByDefault);
	const [revealableCount, setRevealableCount] = useState(0);
	const [labelCount, setLabelCount] = useState(0);
	const resetTimerRef = useRef(void 0);
	const generatedId = useId();
	const labelId = `${generatedId}-label`;
	const boxId = `${generatedId}-box`;
	useEffect(() => () => clearTimeout(resetTimerRef.current), []);
	const handleCopy = useCallback(() => {
		const selection = window.getSelection();
		const boxEl = document.getElementById(boxId);
		if (selection && !selection.isCollapsed && selection.anchorNode && boxEl?.contains(selection.anchorNode)) return;
		copyToClipboard(value).then(() => {
			onCopy?.();
			setCopied(true);
			clearTimeout(resetTimerRef.current);
			resetTimerRef.current = setTimeout(() => setCopied(false), 1500);
		}).catch((error) => {
			console.error("Copy to clipboard failed", error);
		});
	}, [
		value,
		onCopy,
		boxId
	]);
	const toggleHidden = useCallback(() => setHidden((prev) => !prev), []);
	const registerRevealable = useCallback(() => {
		setRevealableCount((count) => count + 1);
		return () => setRevealableCount((count) => count - 1);
	}, []);
	const registerLabel = useCallback(() => {
		setLabelCount((count) => count + 1);
		return () => setLabelCount((count) => count - 1);
	}, []);
	const contextValue = useMemo(() => ({
		copied,
		handleCopy,
		value,
		mono,
		hidden,
		toggleHidden,
		registerRevealable,
		hasRevealable: revealableCount > 0,
		registerLabel,
		hasLabel: labelCount > 0,
		minLines,
		maxLines: maxLines ?? minLines,
		hoverHint,
		labelId,
		boxId
	}), [
		copied,
		handleCopy,
		value,
		mono,
		hidden,
		toggleHidden,
		registerRevealable,
		revealableCount,
		registerLabel,
		labelCount,
		minLines,
		maxLines,
		hoverHint,
		labelId,
		boxId
	]);
	return /* @__PURE__ */ jsx(CopyContext, {
		value: contextValue,
		children: /* @__PURE__ */ jsx("div", {
			className: cn("tw:flex tw:w-fit tw:max-w-full tw:flex-col tw:gap-1", className),
			children
		})
	});
};
//#endregion
//#region src/blocks/CopyField/CopySuffix.tsx
/** Muted, non-selectable suffix addon with a leading divider (not part of the copied value). */
const CopySuffix = ({ children, className }) => /* @__PURE__ */ jsx("span", {
	"aria-hidden": true,
	className: cn("tw:shrink-0 tw:select-none tw:self-stretch tw:border-l tw:border-border tw:pl-2 tw:text-muted-foreground", className),
	children
});
//#endregion
//#region src/blocks/CopyField/CopyField.tsx
/**
* Readonly value/command box that copies `value` on click.
*
* - A click that ends a text selection does not copy.
* - `mono` for commands; `maxLines > 1` wraps and scrolls between `minLines` and `maxLines`.
* - `CopyField.Hidden` masks content; copy still copies the real `value`.
*/
const CopyField = {
	/** Owns `value`, mask and copy state. */
	Root: CopyRoot,
	/** Label above the box, with `leading`/`trailing` slots. */
	Label: CopyLabel,
	/** The clickable copy target. */
	Box: CopyBox,
	/** Not copied. */
	Prefix: CopyPrefix,
	/** The displayed value. */
	Children: CopyChildren,
	/** Not copied. */
	Suffix: CopySuffix,
	/** Masked display only; copy still copies the real value. */
	Hidden: CopyHidden,
	/** Toggles revealable `CopyField.Hidden` content. */
	Reveal: CopyReveal,
	/** Copy icon with Copy → Check feedback. */
	Button: CopyButton,
	/** Helper text below the box. */
	Footer: CopyFooter
};
//#endregion
export { CopyField };

//# sourceMappingURL=CopyField-D0Pg4Z0R.js.map